import React, { useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import { Trans, useTranslation } from "react-i18next";
import { getValueFromInput } from "util/FormsUtils";
import { ControlLabel, FormControl, FormGroup, HelpBlock, InputGroup } from "components/bootstrap";
import { URLAllowListInput, SourceCodeEditor } from "components/common";
import DocsHelper from "util/DocsHelper";
import DocumentationLink from "components/support/DocumentationLink";
export const defaultConfig = {
  webhook_url: "",
  /* eslint-disable no-template-curly-in-string */
  adaptive_card: '{\n  "type": "message",\n  "attachments": [\n    {\n      "contentType": "application/vnd.microsoft.card.adaptive",\n      "content": {\n        "type": "AdaptiveCard",\n        "version": "1.5",\n        "msTeams": { "width": "full" },\n        "body": [\n          {\n            "type": "TextBlock",\n            "size": "Large",\n            "weight": "Bolder",\n            "text": "${event_definition_title} triggered",\n            "style": "heading",\n            "fontType": "Default"\n          },\n          {\n            "type": "TextBlock",\n            "text": "${event_definition_description}",\n            "wrap": true\n          },\n          {\n            "type": "TextBlock",\n            "text": "Event Details",\n            "wrap": true\n          },\n          {\n            "type": "FactSet",\n            "facts": [\n              {\n                "title": "Type",\n                "value": "${event_definition_type}"\n              },\n              {\n                "title": "Timestamp",\n                "value": "{{DATE(${event.timestamp_processing},SHORT)}} at {{TIME(${event.timestamp_processing})}}"\n              },\n              {\n                "title": "Message",\n                "value": "${event.message}"\n              },\n              {\n                "title": "Source",\n                "value": "${event.source}"\n              },\n              {\n                "title": "Key",\n                "value": "${event.key}"\n              },\n              {\n                "title": "Priority",\n                "value": "${event.priority}"\n              },\n              {\n                "title": "Alert",\n                "value": "${event.alert}"\n              },\n              {\n                "title": "Timerange Start",\n                "value": "${event.timerange_start}"\n              },\n              {\n                "title": "Timerange End",\n                "value": "${event.timerange_end}"\n              }\n            ]\n          }${if event.fields},\n          {\n            "type": "TextBlock",\n            "text": "Event Fields",\n            "weight": "bolder",\n            "size": "medium"\n          },\n          {\n            "type": "FactSet",\n            "facts": [${foreach event.fields field}\n              { "title": "${field.key}", "value": "${field.value}" }${if last_field}${else},${end}${end}\n            ]\n          }${end}${if backlog},\n          {\n            "type": "TextBlock",\n            "text": "Backlog",\n            "weight": "bolder",\n            "size": "medium"\n          },\n          {\n            "type": "FactSet",\n            "facts": [${foreach backlog message}\n              { "title": "Message", "value": "${message.message}" }${if last_message}${else},${end}${end}\n            ]\n          }${end}\n        ],\n        "actions": [{\n          "type": "Action.OpenUrl",\n          "title": "Replay Search",\n          "url": "${http_external_uri}alerts/${event.id}/replay-search"\n        }],\n        "$schema": "http://adaptivecards.io/schemas/adaptive-card.json",\n        "rtl": false\n      }\n    }\n  ]\n}',
  /* eslint-enable no-template-curly-in-string */
  backlog_size: 0
};
const TeamsNotificationV2Form = ({ config, validation, onChange }) => {
  var _a, _b, _c, _d, _e, _f;
  const { t } = useTranslation("integrations");
  const defaultBacklogSize = config.backlog_size;
  const [isBacklogSizeEnabled, setIsBacklogSizeEnabled] = useState(defaultBacklogSize > 0);
  const [backlogSize, setBacklogSize] = useState(defaultBacklogSize);
  const propagateChange = (key, value) => {
    const nextConfig = cloneDeep(config);
    nextConfig[key] = value;
    onChange(nextConfig);
  };
  const handleBacklogSizeChange = (event) => {
    const { name } = event.target;
    const value = Number(getValueFromInput(event.target));
    setBacklogSize(value);
    propagateChange(name, value);
  };
  const toggleBacklogSize = () => {
    setIsBacklogSizeEnabled((prevIsBacklogSizeEnabled) => {
      const nextIsBacklogSizeEnabled = !prevIsBacklogSizeEnabled;
      propagateChange("backlog_size", prevIsBacklogSizeEnabled ? 0 : backlogSize);
      return nextIsBacklogSizeEnabled;
    });
  };
  const handleWebhookUrlChange = (event) => {
    propagateChange("webhook_url", getValueFromInput(event.target));
  };
  const handleAdaptiveCardChange = (nextValue) => {
    propagateChange("adaptive_card", nextValue);
  };
  const element = /* @__PURE__ */ React.createElement("p", null, /* @__PURE__ */ React.createElement(
    Trans,
    {
      t,
      i18nKey: "teams_notification_v2_form.adaptive_card_help",
      components: {
        docsLink: /* @__PURE__ */ React.createElement(DocumentationLink, { page: DocsHelper.PAGES.ALERTS, text: t("slack_notification_form.docs_link_text") })
      }
    }
  ));
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    URLAllowListInput,
    {
      label: t("teams_notification_v2_details.webhook_url_label"),
      onChange: handleWebhookUrlChange,
      validationState: validation.errors.webhook_url ? "error" : null,
      validationMessage: (_c = (_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.webhook_url) == null ? void 0 : _b[0]) != null ? _c : t("teams_notification_form.webhook_url_help"),
      url: config.webhook_url || "",
      autofocus: false
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("teams_notification_v2_details.adaptive_card_label")), /* @__PURE__ */ React.createElement(
    SourceCodeEditor,
    {
      id: "notification-adaptiveCard",
      mode: "text",
      theme: "light",
      value: config.adaptive_card || "",
      wrapEnabled: true,
      onChange: handleAdaptiveCardChange
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, (_f = (_e = (_d = validation == null ? void 0 : validation.errors) == null ? void 0 : _d.adaptive_card) == null ? void 0 : _e[0]) != null ? _f : element)), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("slack_notification_form.backlog_limit_label")), /* @__PURE__ */ React.createElement(InputGroup, null, /* @__PURE__ */ React.createElement(InputGroup.Addon, null, /* @__PURE__ */ React.createElement(
    "input",
    {
      id: "toggle_backlog_size",
      "aria-label": t("teams_notification_v2_form.backlog_toggle_aria_label"),
      type: "checkbox",
      checked: isBacklogSizeEnabled,
      onChange: toggleBacklogSize
    }
  )), /* @__PURE__ */ React.createElement(
    FormControl,
    {
      type: "number",
      id: "backlog_size",
      name: "backlog_size",
      onChange: handleBacklogSizeChange,
      value: backlogSize,
      min: "0",
      disabled: !isBacklogSizeEnabled
    }
  )), /* @__PURE__ */ React.createElement(HelpBlock, null, t("teams_notification_form.backlog_limit_help"))));
};
export default TeamsNotificationV2Form;
